import { useState, type FormEvent } from 'react';
import { Moon, Sun } from 'lucide-react';
import lockup from '@/assets/asya-lockup.png';
import lockupDark from '@/assets/asya-lockup-dark.png';
import { ERROR_CODES, isMfaChallenge, type MfaChallenge } from '@asyagrup/group-hub-contracts';
import { useSession } from '@/app/session-context';
import { useTheme } from '@/app/theme';
import { ApiError, errorMessage } from '@/lib/api/client';
import { Button, ErrorState, Field, IconButton, Input } from '@/ui/primitives';
import { ThemedImage } from '@/ui/ThemedImage';
import { RegisterPage } from './RegisterPage';

export function LoginPage() {
  /**
   * Kayıt ekranı bir rota değil, bu ekranın ikinci hâli: uygulama giriş
   * yapılmamışken router'ı hiç çalıştırmıyor (`status === 'anonymous'` ise
   * doğrudan bu bileşen basılıyor). Açık bir rota eklemek, kimlik varsayan bir
   * kabuğa kimliksiz bir sayfa iliştirmek olurdu.
   */
  const [registering, setRegistering] = useState(false);
  const { login, completeMfa } = useSession();
  const { theme, toggle } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /** Non-null once the password has been accepted and a second factor is owed. */
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (registering) return <RegisterPage onBack={() => setRegistering(false)} />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await login(email, password);
      if (isMfaChallenge(res)) {
        setChallenge(res);
        // The password has done its job. Holding it in state for the second leg
        // would keep it in memory for the life of the page for no reason —
        // `mfaToken` is what proves the first factor from here on.
        setPassword('');
      }
    } catch (err) {
      // `errorMessage` rather than this screen's own copy of the same two
      // branches. It had one — the server's message for an ApiError, a Turkish
      // network line for anything else — written before the shared helper
      // learned the second half. The two wordings had already drifted by a
      // clause, and this is the screen where the network is most likely to be
      // the actual problem: nobody is signed in yet, so nothing else has had a
      // chance to fail first.
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitCode(event: FormEvent) {
    event.preventDefault();
    if (!challenge) return;
    setError(null);
    setBusy(true);
    try {
      await completeMfa(challenge.mfaToken, code.trim());
    } catch (err) {
      setError(errorMessage(err));
      // The mfaToken lives five minutes. Once it expires no code will ever work,
      // and leaving someone typing into a dead form is how a login screen earns
      // a support call — send them back to the first step. A merely wrong code
      // (MFA_INVALID) keeps the form, because retyping six digits is the whole
      // expected failure here.
      if (err instanceof ApiError && err.code === ERROR_CODES.TOKEN_EXPIRED) {
        setChallenge(null);
        setCode('');
      }
    } finally {
      setBusy(false);
    }
  }

  function backToPassword() {
    setChallenge(null);
    setCode('');
    setError(null);
  }

  return (
    <div className="bg-bg pt-safe pb-safe px-safe flex min-h-dvh flex-col">
      <header className="flex justify-end p-4">
        <IconButton
          onClick={toggle}
          aria-label={theme === 'dark' ? 'Açık temaya geç' : 'Koyu temaya geç'}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </IconButton>
      </header>

      {/*
       * Centred vertically only from `md:` up.
       *
       * On a phone the keyboard covers the lower half of the screen the moment
       * the e-mail field is tapped; a vertically centred card is then pushed
       * half off the top with nothing left to scroll back to. Anchored to the
       * top, the card keeps its position and the page can scroll if it needs to.
       */}
      <main className="flex flex-1 justify-center px-4 pb-8 md:items-center md:pb-16">
        <div className="w-full max-w-sm">
          <div className="mb-6 text-center md:mb-8">
            {/* The full lockup rather than the badge: this is the screen an
                outsider meets first, and it is the one place with room for the
                wordmark to be read rather than recognised.

                Two files, because the lockup is mostly navy and the dark
                theme's surface is the same navy — see ThemedImage. The header
                was fixed first and this screen was not, which left the defect
                live on the one page that needs no account to reach. */}
            <ThemedImage
              light={lockup}
              dark={lockupDark}
              alt="ASYA ASC HAVACILIK"
              className="mx-auto mb-4 h-20 w-auto"
            />
            <h1 className="text-fg text-xl font-semibold">ASYA GRUP</h1>
            <p className="text-fg-muted mt-1 text-sm">Merkezi İnsan Kaynakları ve Finans</p>
          </div>

          {challenge === null ? (
            <form
              onSubmit={onSubmit}
              className="border-border bg-surface space-y-4 rounded-xl border p-4 shadow-sm md:p-6"
            >
              <Field label="E-posta" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="placeholder:text-fg-muted"
                  placeholder="ad.soyad@asyahavacilik.com"
                />
              </Field>

              <Field label="Parola" htmlFor="password">
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>

              {error && <ErrorState message={error} />}

              <Button type="submit" variant="primary" className="w-full" busy={busy}>
                {busy ? 'Giriş yapılıyor…' : 'Giriş yap'}
              </Button>
            </form>
          ) : (
            <form
              onSubmit={onSubmitCode}
              className="border-border bg-surface space-y-4 rounded-xl border p-4 shadow-sm md:p-6"
            >
              <div>
                <h2 className="text-fg text-sm font-semibold">İki adımlı doğrulama</h2>
                <p className="text-fg-muted mt-1 text-xs">
                  Doğrulama uygulamanızdaki 6 haneli kodu girin. Uygulamaya erişemiyorsanız kurtarma
                  kodlarınızdan birini de kullanabilirsiniz.
                </p>
              </div>

              <Field label="Doğrulama kodu" htmlFor="mfa-code">
                <Input
                  id="mfa-code"
                  // Not `inputMode="numeric"`: a recovery code is letters and a
                  // dash, and a numeric keypad would leave the one person who
                  // has lost their phone unable to type the thing that exists
                  // for exactly that moment.
                  autoComplete="one-time-code"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="placeholder:text-fg-muted font-mono tracking-widest"
                  placeholder="000000"
                />
              </Field>

              {error && <ErrorState message={error} />}

              <Button type="submit" variant="primary" className="w-full" busy={busy}>
                {busy ? 'Doğrulanıyor…' : 'Doğrula ve gir'}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={backToPassword}
                disabled={busy}
              >
                Geri dön
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Button type="button" variant="ghost" onClick={() => setRegistering(true)}>
              Hesabınız yok mu? Üye ol
            </Button>
            <p className="text-fg-muted mt-2 text-xs">
              Erişim sorunu için insan kaynakları ile görüşün.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
